import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import '../css/PickOneAutoTest.css';

const STORAGE_KEY = 'admin_pick_one_auto_sandbox_v1';
const RANKS = ['BG', 'BGM', 'NB', 'NB+', 'N-', 'N', 'N/S', 'S', 'S/P'];
const INTENSITIES = ['LIGHT', 'MEDIUM', 'HEAVY', 'ANY'];
const INTENSITY_META = {
  LIGHT: { label: 'เบา', icon: '🟢', level: 0 },
  MEDIUM: { label: 'กลาง', icon: '🟡', level: 1 },
  HEAVY: { label: 'หนัก', icon: '🔴', level: 2 },
  ANY: { label: 'ได้ทุกระดับ', icon: '⚪', level: null }
};
const EMPTY_DATA = { sequence: 0, matchSequence: 0, players: [], queue: [], playing: [], history: [], pairs: [], logs: [] };

const readStorage = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return value && Array.isArray(value.players) ? { ...EMPTY_DATA, ...value } : EMPTY_DATA;
  } catch {
    return EMPTY_DATA;
  }
};

const intensityCompatible = players => {
  const levels = players.map(player => INTENSITY_META[player.intensity]?.level).filter(Number.isFinite);
  return levels.length < 2 || Math.max(...levels) - Math.min(...levels) <= 1;
};

const intensityPriority = (anchor, candidate) => {
  const anchorRankIndex = RANKS.indexOf(anchor.rank);
  const candidateRankIndex = RANKS.indexOf(candidate.rank);
  if (candidateRankIndex < anchorRankIndex) {
    if (candidate.intensity === 'HEAVY') return 0;
    if (candidate.intensity === 'MEDIUM' || candidate.intensity === 'ANY') return 1;
    return 2;
  }
  if (candidateRankIndex > anchorRankIndex) {
    if (candidate.intensity === 'MEDIUM') return 0;
    if (candidate.intensity === 'HEAVY' || candidate.intensity === 'ANY') return 1;
    return 2;
  }
  if (anchor.intensity === 'ANY') {
    if (candidate.intensity === 'MEDIUM') return 0;
    if (candidate.intensity === 'ANY') return 1;
    return 2;
  }
  if (candidate.intensity === anchor.intensity) return 0;
  if (candidate.intensity === 'ANY') return 1;
  return 2;
};

const pairKey = (left, right) => [String(left), String(right)].sort().join('::');
const respectsPairSelection = (players, pairs) => pairs.every(pair => {
  const count = [pair.userA, pair.userB].filter(id => players.some(player => player.id === id)).length;
  return count === 0 || count === 2;
});

const bestTeams = (players, pairs = []) => {
  const layouts = [
    [[players[0], players[1]], [players[2], players[3]]],
    [[players[0], players[2]], [players[1], players[3]]],
    [[players[0], players[3]], [players[1], players[2]]]
  ];
  return layouts.filter(([team1, team2]) => pairs.every(pair => {
    const selected = players.some(player => player.id === pair.userA) || players.some(player => player.id === pair.userB);
    if (!selected) return true;
    return [team1, team2].some(team => team.some(player => player.id === pair.userA) && team.some(player => player.id === pair.userB));
  })).map(([team1, team2]) => ({
    team1,
    team2,
    gap: Math.abs(team1.reduce((sum, player) => sum + player.point, 0) - team2.reduce((sum, player) => sum + player.point, 0))
  })).sort((left, right) => left.gap - right.gap)[0] || null;
};

const buildAutoMatch = (activePlayers, requestedAnchor, pairs = []) => {
  const anchor = requestedAnchor || [...activePlayers].sort((left, right) => left.games - right.games || left.id.localeCompare(right.id))[0];
  if (!anchor) return null;
  const candidates = activePlayers
    .filter(player => player.id !== anchor.id)
    .filter(player => intensityCompatible([anchor, player]))
    .sort((left, right) =>
      Number(left.rank !== anchor.rank) - Number(right.rank !== anchor.rank) ||
      Math.abs(RANKS.indexOf(left.rank) - RANKS.indexOf(anchor.rank)) - Math.abs(RANKS.indexOf(right.rank) - RANKS.indexOf(anchor.rank)) ||
      intensityPriority(anchor, left) - intensityPriority(anchor, right) ||
      Math.abs(left.point - anchor.point) - Math.abs(right.point - anchor.point) ||
      left.games - right.games
    );
  for (let first = 0; first < candidates.length; first += 1) {
    for (let second = first + 1; second < candidates.length; second += 1) {
      for (let third = second + 1; third < candidates.length; third += 1) {
        const group = [anchor, candidates[first], candidates[second], candidates[third]];
        if (!intensityCompatible(group) || !respectsPairSelection(group, pairs)) continue;
        const teams = bestTeams(group, pairs);
        if (teams) return { ...teams, anchorId: anchor.id };
      }
    }
  }
  return null;
};

function PlayerChip({ player }) {
  const intensity = INTENSITY_META[player.intensity] || INTENSITY_META.ANY;
  return <span className="poat-player-chip">
    <b>{player.name}</b><small>Rank {player.rank} · {intensity.icon} {intensity.label} · {player.point} คะแนน</small>
  </span>;
}

function MatchTeams({ match }) {
  return <div className="poat-teams">
    <div><strong>Team 1</strong>{match.team1.map(player => <PlayerChip key={player.id} player={player} />)}</div>
    <b className="poat-vs">VS</b>
    <div><strong>Team 2</strong>{match.team2.map(player => <PlayerChip key={player.id} player={player} />)}</div>
  </div>;
}

export default function PickOneAutoTest() {
  const navigate = useNavigate();
  const [actor] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [data, setData] = useState(readStorage);
  const [rank, setRank] = useState('N');
  const [intensity, setIntensity] = useState('MEDIUM');
  const [generateCount, setGenerateCount] = useState(10);
  const [anchorId, setAnchorId] = useState('AUTO');
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [autoAiEnabled, setAutoAiEnabled] = useState(false);
  const [pairA, setPairA] = useState('');
  const [pairB, setPairB] = useState('');
  const [pairType, setPairType] = useState('WAITING');
  const [logPlayerId, setLogPlayerId] = useState('ALL');

  useEffect(() => {
    if (!actor || String(actor.role).toLowerCase() !== 'admin') navigate('/', { replace: true });
  }, [actor, navigate]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  const activePlayers = useMemo(() => data.players.filter(player => player.status === 'ACTIVE'), [data.players]);
  const anchor = anchorId === 'AUTO'
    ? [...activePlayers].sort((left, right) => left.games - right.games || left.id.localeCompare(right.id))[0] || null
    : activePlayers.find(player => String(player.id) === String(anchorId)) || null;

  const generatePlayers = () => {
    setData(current => {
      const players = Array.from({ length: generateCount }, (_, index) => {
        const number = current.sequence + index + 1;
        const selectedRank = rank === 'RANDOM' ? RANKS[number % RANKS.length] : rank;
        const selectedIntensity = intensity === 'RANDOM' ? INTENSITIES[number % INTENSITIES.length] : intensity;
        const rankIndex = RANKS.indexOf(selectedRank);
        return {
          id: `test-${number}`,
          name: `ผู้เล่นทดสอบ ${String(number).padStart(3, '0')}`,
          rank: selectedRank,
          intensity: selectedIntensity,
          point: 320 + rankIndex * 48 + (number % 5) * 6,
          games: 0,
          status: 'ACTIVE'
        };
      });
      const logs = players.map(player => ({ id: `${Date.now()}-${player.id}`, playerId: player.id, playerName: player.name, type: 'CREATE', detail: `สร้าง Rank ${player.rank} · ${INTENSITY_META[player.intensity].label}`, at: new Date().toISOString() }));
      return { ...current, sequence: current.sequence + generateCount, players: [...current.players, ...players], logs: [...logs, ...(current.logs || [])].slice(0, 500) };
    });
    setMessage(`สร้างผู้เล่นจำลอง ${generateCount} คนแล้ว`);
  };

  const createPreview = () => {
    if (!anchor) return setMessage('กรุณาเลือกผู้เล่นหลักก่อน');
    const teams = buildAutoMatch(activePlayers, anchor, data.pairs || []);
    if (!teams) {
      setPreview(null);
      return setMessage('ยังไม่มีผู้เล่นที่ผ่านกฎ Rank/ความหนัก และ Waiting Pair/Locked Pair');
    }
    setPreview(teams);
    setMessage(`Preview สำเร็จ · ผลรวมทีมต่างกัน ${teams.gap} คะแนน`);
  };

  const addPreviewToQueue = () => {
    if (!preview) return;
    setData(current => {
      const matchNo = current.matchSequence + 1;
      const ids = [...preview.team1, ...preview.team2].map(player => player.id);
      const queueMatch = { ...preview, id: `local-match-${matchNo}`, matchNo, createdAt: new Date().toISOString() };
      return {
        ...current,
        matchSequence: matchNo,
        players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'QUEUE' } : player),
        queue: [...current.queue, queueMatch],
        logs: [
          ...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-queue`, playerId: id, playerName: player?.name || id, type: 'QUEUE', detail: `เข้า Match #${matchNo}`, at: new Date().toISOString() }; }),
          ...(current.logs || [])
        ].slice(0, 500)
      };
    });
    setPreview(null);
    setAnchorId('AUTO');
    setMessage('เพิ่ม Match เข้า Queue แล้ว');
  };

  const startMatch = matchId => setData(current => {
    const match = current.queue.find(item => item.id === matchId);
    if (!match) return current;
    const ids = [...match.team1, ...match.team2].map(player => player.id);
    return {
      ...current,
      players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'PLAY' } : player),
      queue: current.queue.filter(item => item.id !== matchId),
      playing: [...current.playing, { ...match, startedAt: new Date().toISOString() }],
      logs: [
        ...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-play`, playerId: id, playerName: player?.name || id, type: 'PLAY', detail: `เริ่ม Match #${match.matchNo}`, at: new Date().toISOString() }; }),
        ...(current.logs || [])
      ].slice(0, 500)
    };
  });

  const cancelQueue = matchId => setData(current => {
    const match = current.queue.find(item => item.id === matchId);
    if (!match) return current;
    const ids = [...match.team1, ...match.team2].map(player => player.id);
    return {
      ...current,
      players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'ACTIVE' } : player),
      queue: current.queue.filter(item => item.id !== matchId)
    };
  });

  const finishMatch = (matchId, result) => setData(current => {
    const match = current.playing.find(item => item.id === matchId);
    if (!match) return current;
    const ids = [...match.team1, ...match.team2].map(player => player.id);
    return {
      ...current,
      players: current.players.map(player => ids.includes(player.id)
        ? { ...player, status: 'ACTIVE', games: player.games + 1 }
        : player),
      playing: current.playing.filter(item => item.id !== matchId),
      history: [{ ...match, result, finishedAt: new Date().toISOString() }, ...current.history].slice(0, 50),
      logs: [
        ...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-finish`, playerId: id, playerName: player?.name || id, type: 'FINISH', detail: `จบ Match #${match.matchNo} · ${result}`, at: new Date().toISOString() }; }),
        ...(current.logs || [])
      ].slice(0, 500)
    };
  });

  useEffect(() => {
    if (!autoAiEnabled) return undefined;
    const timer = window.setInterval(() => setData(current => {
      if (current.playing.length > 0) return current;
      if (current.queue.length > 0) {
        const match = current.queue[0];
        const ids = [...match.team1, ...match.team2].map(player => player.id);
        return {
          ...current,
          players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'PLAY' } : player),
          queue: current.queue.slice(1),
          playing: [...current.playing, { ...match, startedAt: new Date().toISOString(), autoAi: true }],
          logs: [...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-auto-play`, playerId: id, playerName: player?.name || id, type: 'AUTO_PLAY', detail: `Auto AI เริ่ม Match #${match.matchNo}`, at: new Date().toISOString() }; }), ...(current.logs || [])].slice(0, 500)
        };
      }
      const available = current.players.filter(player => player.status === 'ACTIVE');
      const autoMatch = buildAutoMatch(available, null, current.pairs || []);
      if (!autoMatch) return current;
      const matchNo = current.matchSequence + 1;
      const ids = [...autoMatch.team1, ...autoMatch.team2].map(player => player.id);
      const match = { ...autoMatch, id: `local-match-${matchNo}`, matchNo, createdAt: new Date().toISOString(), autoAi: true };
      return {
        ...current,
        matchSequence: matchNo,
        players: current.players.map(player => ids.includes(player.id) ? { ...player, status: 'QUEUE' } : player),
        queue: [...current.queue, match],
        logs: [...ids.map(id => { const player = current.players.find(item => item.id === id); return { id: `${Date.now()}-${id}-auto-queue`, playerId: id, playerName: player?.name || id, type: 'AUTO_QUEUE', detail: `Auto AI สร้าง Match #${matchNo}`, at: new Date().toISOString() }; }), ...(current.logs || [])].slice(0, 500)
      };
    }), 900);
    return () => window.clearInterval(timer);
  }, [autoAiEnabled]);

  const addProtectedPair = () => {
    if (!pairA || !pairB || pairA === pairB) return setMessage('กรุณาเลือกผู้เล่น 2 คนที่ไม่ซ้ำกัน');
    if ((data.pairs || []).some(pair => [pair.userA, pair.userB].some(id => id === pairA || id === pairB))) return setMessage('ผู้เล่นคนใดคนหนึ่งมีคู่บังคับอยู่แล้ว');
    const playerA = data.players.find(player => player.id === pairA);
    const playerB = data.players.find(player => player.id === pairB);
    setData(current => ({ ...current, pairs: [...(current.pairs || []), { id: pairKey(pairA, pairB), userA: pairA, userB: pairB, nameA: playerA?.name, nameB: playerB?.name, type: pairType }] }));
    setPairA(''); setPairB('');
    setMessage(`เพิ่ม ${pairType === 'LOCKED' ? 'Locked Pair' : 'Waiting Pair'} แล้ว`);
  };

  const removeProtectedPair = pairId => setData(current => ({ ...current, pairs: (current.pairs || []).filter(pair => pair.id !== pairId) }));

  const visibleLogs = (data.logs || []).filter(log => logPlayerId === 'ALL' || log.playerId === logPlayerId);

  const resetSandbox = () => {
    if (!window.confirm('ล้างข้อมูลทดสอบทั้งหมดในเครื่องนี้?')) return;
    setData(EMPTY_DATA);
    setPreview(null);
    setAnchorId('AUTO');
    setMessage('ล้างข้อมูลทดสอบแล้ว');
  };

  if (!actor || String(actor.role).toLowerCase() !== 'admin') return null;

  return <main className="admin-shell-with-sidebar poat-shell">
    <AdminSidebar active="pick-one-test" />
    <div className="poat-page">
      <header className="poat-hero"><div><small>LOCAL SANDBOX · ADMIN ONLY</small><h1>ทดสอบระบบ Pick One Auto</h1><p>ข้อมูลทั้งหมดอยู่ใน localStorage ไม่ส่งไป Backend และไม่บันทึก Database</p></div><button type="button" onClick={resetSandbox}>🗑 ล้างข้อมูลทดสอบ</button></header>

      {message && <div className="poat-message" role="status">{message}<button type="button" onClick={() => setMessage('')}>×</button></div>}

      <section className="poat-generator poat-card">
        <div className="poat-title"><span>1</span><div><h2>สร้างผู้เล่นจำลอง</h2><p>เลือกจำนวน Rank และความหนักของผู้เล่นชุดใหม่</p></div><b>{data.players.length} คน</b></div>
        <div className="poat-generator-controls">
          <label><span>จำนวนผู้เล่น</span><select value={generateCount} onChange={event => setGenerateCount(Number(event.target.value))}>{[3,5,7,10,15,20].map(count => <option key={count} value={count}>{count} คน</option>)}</select></label>
          <label><span>ระดับมือ</span><select value={rank} onChange={event => setRank(event.target.value)}><option value="RANDOM">สุ่มทุก Rank</option>{RANKS.map(item => <option key={item} value={item}>Rank {item}</option>)}</select></label>
          <label><span>ความหนัก</span><select value={intensity} onChange={event => setIntensity(event.target.value)}><option value="RANDOM">สุ่มทุกระดับ</option>{INTENSITIES.map(item => <option key={item} value={item}>{INTENSITY_META[item].icon} {INTENSITY_META[item].label}</option>)}</select></label>
          <button type="button" onClick={generatePlayers}>＋ สร้างผู้เล่น {generateCount} คน</button>
        </div>
      </section>

      <section className="poat-builder poat-card">
        <div className="poat-title"><span>2</span><div><h2>Pick One Auto</h2><p>Rank เดียวกันก่อน · ข้าม Rank ใกล้ที่สุด · รักษาคู่บังคับตลอด</p></div><b>{activePlayers.length} ACTIVE</b></div>
        <div className="poat-anchor-row"><label><span>ผู้เล่นหลัก</span><select value={anchorId} onChange={event => { setAnchorId(event.target.value); setPreview(null); }}><option value="AUTO">🤖 Auto เลือกคนเกมน้อยที่สุด</option>{activePlayers.map(player => <option key={player.id} value={player.id}>{player.name} · {player.rank} · {INTENSITY_META[player.intensity].label}</option>)}</select></label><button type="button" onClick={createPreview} disabled={!anchor}>🤖 สร้าง Preview</button></div>
        {preview && <article className="poat-preview"><header><strong>Match Preview</strong><b>Gap {preview.gap} คะแนน</b></header><MatchTeams match={preview} /><button type="button" onClick={addPreviewToQueue}>＋ เพิ่มเข้า Match Queue</button></article>}
      </section>

      <section className="poat-card poat-automation">
        <div className="poat-title"><span>AI</span><div><h2>Auto AI Generate & Play</h2><p>AI เลือกผู้เล่นหลัก สร้าง Queue และเริ่ม Match เอง ผู้ใช้กดเฉพาะผลจบ Match</p></div><b>{autoAiEnabled ? 'RUNNING' : 'STOPPED'}</b></div>
        <button type="button" className={autoAiEnabled ? 'is-running' : ''} onClick={() => setAutoAiEnabled(value => !value)}>{autoAiEnabled ? '■ หยุด Auto AI' : '▶ เริ่ม Auto AI'}</button>
      </section>

      <section className="poat-card poat-pairs">
        <div className="poat-title"><span>🔗</span><div><h2>Waiting Pair / Locked Pair</h2><p>คู่ที่กำหนดจะถูกเลือกพร้อมกันและอยู่ทีมเดียวกันในทุก Match</p></div><b>{(data.pairs || []).length} คู่</b></div>
        <div className="poat-pair-form"><select value={pairType} onChange={event => setPairType(event.target.value)}><option value="WAITING">Waiting Pair</option><option value="LOCKED">Locked Pair</option></select><select value={pairA} onChange={event => setPairA(event.target.value)}><option value="">ผู้เล่นคนที่ 1</option>{data.players.map(player => <option key={player.id} value={player.id}>{player.name}</option>)}</select><select value={pairB} onChange={event => setPairB(event.target.value)}><option value="">ผู้เล่นคนที่ 2</option>{data.players.filter(player => player.id !== pairA).map(player => <option key={player.id} value={player.id}>{player.name}</option>)}</select><button type="button" onClick={addProtectedPair}>＋ เพิ่มคู่</button></div>
        <div className="poat-pair-list">{(data.pairs || []).map(pair => <span key={pair.id}><b>{pair.type}</b>{pair.nameA} + {pair.nameB}<button type="button" onClick={() => removeProtectedPair(pair.id)}>×</button></span>)}</div>
      </section>

      <div className="poat-board">
        <section className="poat-card"><div className="poat-title"><span>3</span><div><h2>Match Queue</h2><p>คิวรอเริ่มการแข่งขัน</p></div><b>{data.queue.length} Match</b></div><div className="poat-match-list">{data.queue.length === 0 && <p className="poat-empty">ยังไม่มี Match Queue</p>}{data.queue.map(match => <article className="poat-match" key={match.id}><header><strong>Match #{match.matchNo}</strong><span>WAITING</span></header><MatchTeams match={match} /><footer><button className="start" onClick={() => startMatch(match.id)}>▶ เริ่ม Match</button><button className="cancel" onClick={() => cancelQueue(match.id)}>ยกเลิก</button></footer></article>)}</div></section>
        <section className="poat-card"><div className="poat-title"><span>4</span><div><h2>Match Playing</h2><p>เลือกผลเพื่อจบและบันทึกประวัติ</p></div><b>{data.playing.length} Match</b></div><div className="poat-match-list">{data.playing.length === 0 && <p className="poat-empty">ยังไม่มี Match กำลังเล่น</p>}{data.playing.map(match => <article className="poat-match is-playing" key={match.id}><header><strong>Match #{match.matchNo}</strong><span>PLAYING</span></header><MatchTeams match={match} /><footer className="results"><button onClick={() => finishMatch(match.id, 'TEAM_1')}>🏆 T1 ชนะ</button><button onClick={() => finishMatch(match.id, 'DRAW')}>🤝 เสมอ</button><button onClick={() => finishMatch(match.id, 'TEAM_2')}>🏆 T2 ชนะ</button></footer></article>)}</div></section>
      </div>

      <section className="poat-card poat-history"><div className="poat-title"><span>5</span><div><h2>ประวัติที่จบแล้ว</h2><p>เก็บล่าสุด 50 Match ใน localStorage</p></div><b>{data.history.length} Match</b></div>{data.history.length === 0 ? <p className="poat-empty">ยังไม่มีประวัติ</p> : <div>{data.history.map(match => <article key={`${match.id}-${match.finishedAt}`}><b>Match #{match.matchNo}</b><span>{match.team1.map(player => player.name).join(' + ')} vs {match.team2.map(player => player.name).join(' + ')}</span><strong>{match.result === 'TEAM_1' ? 'T1 ชนะ' : match.result === 'TEAM_2' ? 'T2 ชนะ' : 'เสมอ'}</strong></article>)}</div>}</section>

      <section className="poat-card poat-logs"><div className="poat-title"><span>LOG</span><div><h2>สรุป Log รายบุคคล</h2><p>ตรวจสอบการสร้าง เข้า Queue เริ่มเล่น และจบ Match ของแต่ละคน</p></div><b>{visibleLogs.length} รายการ</b></div><label><span>กรองผู้เล่น</span><select value={logPlayerId} onChange={event => setLogPlayerId(event.target.value)}><option value="ALL">ผู้เล่นทั้งหมด</option>{data.players.map(player => <option key={player.id} value={player.id}>{player.name} · {player.games} เกม</option>)}</select></label><div className="poat-log-list">{visibleLogs.length === 0 ? <p className="poat-empty">ยังไม่มี Log</p> : visibleLogs.map(log => <article key={log.id}><time>{new Date(log.at).toLocaleString('th-TH')}</time><b>{log.playerName}</b><strong>{log.type}</strong><span>{log.detail}</span></article>)}</div></section>
    </div>
  </main>;
}
